import { randomId } from "@excalidraw/common";
import {
  convertToExcalidrawElements,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import type { ShapeDefinition } from "./shapes/types";

/**
 * Insert a shape into the scene at the given client (viewport) coordinates.
 * The shape is centered on the drop point.
 */
export const insertShapeAtClientPoint = (
  api: ExcalidrawImperativeAPI,
  shape: ShapeDefinition,
  clientX: number,
  clientY: number,
) => {
  const appState = api.getAppState();
  const scenePoint = viewportCoordsToSceneCoords(
    { clientX, clientY },
    {
      zoom: appState.zoom,
      offsetLeft: appState.offsetLeft,
      offsetTop: appState.offsetTop,
      scrollX: appState.scrollX,
      scrollY: appState.scrollY,
    },
  );

  const originX = scenePoint.x - shape.width / 2;
  const originY = scenePoint.y - shape.height / 2;

  const groupId = randomId();
  const newElements = shape.buildElements
    ? shape.buildElements(groupId, originX, originY)
    : convertToExcalidrawElements(
        shape.build ? shape.build(groupId, originX, originY) : [],
      );

  const existing = api.getSceneElementsIncludingDeleted();
  api.updateScene({
    elements: [...existing, ...newElements],
  });

  // Select the newly-inserted group so the user can immediately move/resize.
  api.updateScene({
    appState: {
      selectedElementIds: Object.fromEntries(
        newElements.map((el) => [el.id, true]),
      ),
      selectedGroupIds: { [groupId]: true },
    },
  });
};

/**
 * Returns true if the given client point is over the Excalidraw canvas
 * (i.e., inside the canvas bounding box and not over a UI overlay).
 */
export const isPointOverCanvas = (
  api: ExcalidrawImperativeAPI,
  clientX: number,
  clientY: number,
): boolean => {
  const appState = api.getAppState();
  const { offsetLeft, offsetTop, width, height } = appState;
  return (
    clientX >= offsetLeft &&
    clientX <= offsetLeft + width &&
    clientY >= offsetTop &&
    clientY <= offsetTop + height
  );
};
